import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import moment from 'moment';
import si from 'react-key-string';

import { getUserHistoriesAPI } from '../api/history';
import { getUserRobotsAPI } from '../api/robot';
import {
  getPiboInfo,
  piboDisconnect,
  piboInit,
  receivePiboEvent,
  speakPibo,
} from '../pibo';

const initialRobot = {
  robots: [],
  errorRobots: null,
};

const initialHistory = {
  selectedRobot: null,
  historyList: [],
  page: 0,
  errorHistory: null,
  command: '',
  pending: false,
};

const initialState = {
  loading: false,
  ...initialRobot,
  ...initialHistory,
};

export const getRobots = createAsyncThunk(
  'historyBot/GET_ROBOTS',
  async ({ userId }, { rejectWithValue }) => {
    try {
      const { result, list: robots } = await getUserRobotsAPI({ userId });
      if (result) {
        return robots || [];
      }
      return rejectWithValue('조회된 결과가 없습니다.');
    } catch (err) {
      const { error } = await err.json();
      if ('code' in error && 'desc' in error) {
        const { desc } = error;
        return rejectWithValue(desc);
      }
      return rejectWithValue(
        '알 수 없는 오류가 발생했습니다. 관리자에게 문의하세요.',
      );
    }
  },
);

const mergedList = (historyList, data, attach = false) => {
  const newData = data.reduce((origin, curr) => {
    const [[key, val]] = Object.entries(curr);
    const dateItemIndex = historyList.findIndex(
      (obj) => Object.keys(obj).findIndex((k) => k === key) >= 0,
    );
    const newObj = {};
    if (dateItemIndex > -1) {
      const originVal = Object.values(historyList[dateItemIndex])[0];
      newObj[key] = attach ? val.concat(originVal) : originVal.concat(val);
      historyList.splice(dateItemIndex, 1, newObj);
    } else {
      origin.push(curr);
    }
    return origin;
  }, []);
  const returnData = attach
    ? newData.concat(historyList)
    : historyList.concat(newData);
  return returnData;
};

export const getHistories = createAsyncThunk(
  'historyBot/GET_HISTORIES',
  async (robotId, { getState, rejectWithValue }) => {
    try {
      const { page, historyList } = getState().historyBot;
      const { result, data } = await getUserHistoriesAPI({
        robotId,
        skip: page,
      });
      if (result) {
        if (page === 0) {
          return { data, page: page + 1 };
        }
        let newData = historyList;
        if (data.length > 0) {
          newData = mergedList([...historyList], data);
          return { data: newData, page: page + 1 };
        }
        return { data: newData, page };
      }
      return rejectWithValue('조회된 히스토리가 없습니다.');
    } catch (err) {
      const { error } = await err.json();
      if ('code' in error && 'desc' in error) {
        const { desc } = error;
        return rejectWithValue(desc);
      }
      return rejectWithValue(
        '알 수 없는 오류가 발생했습니다. 관리자에게 문의하세요.',
      );
    }
  },
);

const historySlice = createSlice({
  name: 'historyBot',
  initialState,
  reducers: {
    init: () => ({ ...initialState }),
    pending: (state) => ({ ...state, pending: true }),
    select: (state, action) => ({
      ...state,
      ...initialHistory,
      selectedRobot: action.payload,
    }),
    deselect: (state) => ({ ...state, ...initialHistory }),
    attachHistories: (state, action) => ({
      ...state,
      pending: false,
      historyList: action.payload,
      command: '',
    }),
    setCommand: (state, action) => ({ ...state, command: action.payload }),
  },
  extraReducers: {
    [getRobots.pending.type]: (state) => ({
      ...state,
      loading: true,
      errorRobots: null,
    }),
    [getRobots.fulfilled.type]: (state, action) => ({
      ...state,
      loading: false,
      robots: action.payload,
    }),
    [getRobots.rejected.type]: (state, action) => ({
      ...initialState,
      errorRobots: action.payload,
    }),
    [getHistories.pending.type]: (state) => ({
      ...state,
      pending: true,
      errorHistory: null,
    }),
    [getHistories.fulfilled.type]: (state, action) => ({
      ...state,
      command: '',
      pending: false,
      historyList: action.payload.data,
      page: action.payload.page,
    }),
    [getHistories.rejected.type]: (state, action) => ({
      ...state,
      ...initialRobot,
      selectedRobot: state.selectedRobot,
      page: state.page,
      errorHistory: action.payload,
    }),
  },
});

const { reducer: HistoryReducer, actions } = historySlice;
export const { init, select, deselect, pending, attachHistories, setCommand } =
  actions;

export const updateList = (d) => (dispatch, getState) => {
  const { selectedRobot, historyList } = getState().historyBot;
  const { date: dateTime, q, a } = d;
  if (q || a) {
    const date = moment(dateTime).format('YYYY-MM-DD');
    const obj = {
      id: si.generate(),
      robotId: selectedRobot.id,
      q,
      a,
      createdAt: new Date(dateTime),
      updatedAt: new Date(dateTime),
      firstTime: dateTime,
      lastTime: dateTime,
    };

    const newData = mergedList([...historyList], [{ [date]: [obj] }], true);
    dispatch(attachHistories(newData));
  }
};

let q;
let a;
let date;

export const selectRobot = (robot) => (dispatch) => {
  const { serial } = robot;
  piboDisconnect();
  // piboInit(['']);
  q = '';
  a = '';
  date = 0;
  piboInit(
    [serial],
    () => dispatch(select({ ...robot, connect: false })),
    async () => {
      const infoData = await getPiboInfo();
      if (infoData && 'config' in infoData) {
        const {
          config: { ip },
        } = infoData;
        receivePiboEvent('talk', (d) => {
          // console.log('receiveEvent');
          // console.log('receive duplicate?', d.q === q && d.a === a && d.date === date);
          if (d.q === q && d.a === a && d.date === date) return;
          // console.log(q, a, date, d);
          ({ q, a, date } = d);
          dispatch(updateList(d));
        });
        dispatch(select({ ...robot, connect: !!ip }));
      } else {
        dispatch(select({ ...robot, connect: false }));
      }
    },
  );
};

export const sendCommand = (cmd) => async (dispatch) => {
  dispatch(pending());
  speakPibo(cmd, (d) => {
    // console.log('sendCommand');
    // console.log('send duplicate?', d.q === q && d.a === a && d.date === date);
    if (d.q === q && d.a === a && d.date === date) return;
    // console.log(q, a, date, d);
    ({ q, a, date } = d);
    dispatch(updateList(d));
  });
};

export default HistoryReducer;
